
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Home, Search, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import TranslatedText from "@/TranslatedText";

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const numberVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  // Random products for 404 page background
  const generateProducts = () => {
    const products = [];
    const productIcons = [
      { icon: "📱", size: "text-xl" },
      { icon: "👕", size: "text-2xl" },
      { icon: "👟", size: "text-xl" },
      { icon: "💻", size: "text-2xl" },
      { icon: "🎧", size: "text-xl" },
      { icon: "⌚", size: "text-lg" },
      { icon: "👜", size: "text-2xl" },
      { icon: "🧢", size: "text-xl" }
    ];

    for (let i = 0; i < 20; i++) {
      const product = {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        rotate: `${Math.random() * 40 - 20}deg`,
        delay: Math.random() * 5,
        icon: productIcons[Math.floor(Math.random() * productIcons.length)]
      };
      products.push(product);
    }
    return products;
  };

  const products = generateProducts();

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-ahavex-darkblue to-gray-900 text-white overflow-hidden relative">
      {/* Background product icons */}
      {mounted && products.map((product, index) => (
        <motion.div
          key={index}
          className="absolute rounded-lg bg-white/5 backdrop-blur-sm p-3 shadow-xl"
          style={{
            left: product.left,
            top: product.top,
            transform: `rotate(${product.rotate})`
          }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ 
            opacity: [0.2, 0.4, 0.2], 
            scale: [0.8, 1, 0.8],
            rotate: product.rotate
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            delay: product.delay,
            ease: "easeInOut"
          }}
        >
          <span className={cn("opacity-50", product.icon.size)}>{product.icon.icon}</span>
        </motion.div>
      ))}

      {/* Main content */}
      <motion.div
        className="z-10 px-6 py-8 md:py-10 max-w-md md:max-w-2xl w-full mx-auto text-center relative"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Shopping bag icon */}
        <motion.div 
          className="absolute -top-6 md:-top-10 left-1/2 -translate-x-1/2 text-ahavex-gold opacity-30"
          variants={itemVariants}
        >
          <ShoppingBag size={isMobile ? 120 : 150} strokeWidth={0.8} />
        </motion.div>

        {/* 404 Numbers */}
        <motion.div 
          className="flex justify-center items-center mt-16 md:mt-20 mb-8 relative"
          variants={numberVariants}
        >
          <div className="relative flex items-center justify-center">
            <div className="text-7xl md:text-9xl font-bold text-white opacity-5 absolute">
              404
            </div>
            <div 
              className={cn(
                "text-7xl md:text-9xl font-bold bg-clip-text text-transparent",
                "bg-gradient-to-r from-ahavex-blue via-ahavex-gold to-ahavex-darkblue"
              )}
            >
              404
            </div>
          </div>
        </motion.div>

        {/* Message */}
        <motion.h1
          className="text-2xl md:text-3xl font-bold mb-3 text-white"
          variants={itemVariants}
        >
          <TranslatedText text="Product Not Found" />
        </motion.h1>
        
        <motion.div
          className="h-1 w-16 md:w-20 mx-auto mb-5 bg-gradient-to-r from-ahavex-blue to-ahavex-gold"
          variants={itemVariants}
        />
        
        <motion.p
          className="text-base md:text-lg text-gray-300 mb-8 max-w-sm md:max-w-md mx-auto"
          variants={itemVariants}
        >
          <TranslatedText
            text="We couldn't find the product or page you were looking for. Our store has many other items you'll love!"
          />
        </motion.p>

        {/* Buttons - stacked on mobile, side by side on desktop */}
        <motion.div 
          className="flex flex-col md:flex-row gap-3 justify-center items-center"
          variants={itemVariants}
        >
          <Button
            variant="default"
            size={isMobile ? "default" : "lg"}
            className="bg-ahavex-blue hover:bg-ahavex-darkblue w-full md:w-auto"
            onClick={() => navigate('/')}
          >
            <Home className="mr-2 h-4 w-4" />
            <TranslatedText text="Continue Shopping" />
          </Button>
          
          <Button
            variant="outline"
            size={isMobile ? "default" : "lg"}
            className="border-ahavex-gold text-ahavex-gold hover:bg-ahavex-gold hover:text-black w-full md:w-auto"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <TranslatedText text="Go Back" />
          </Button>
          
          <Button
            variant="ghost"
            size={isMobile ? "default" : "lg"}
            className="text-white hover:bg-white/10 w-full md:w-auto"
            onClick={() => navigate('/?search=true')}
          >
            <Search className="mr-2 h-4 w-4" />
            <TranslatedText text="Search Products" />
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NotFound;
